/**
 * Quick-Nav Registry — curated CORE features per persona context.
 *
 * Feeds the <ContextQuickNav> bar rendered just under the TopBar: 5-8 flagship
 * pages per context so users are never lost in the (huge) left nav. This is a
 * SHORTCUT layer, not a copy of the sidebar — curate ruthlessly.
 *
 * Curating rules:
 *  - 5 to 8 items max per context (MAX_QUICK_NAV_ITEMS enforced).
 *  - hrefs MUST exist in nav-registry.ts (same routes the sidebar links to).
 *  - `badge` is an optional attention chip (e.g. 'Nouveau' on create surfaces).
 *  - If a context has no curated entry, the first leaf routes of its nav tree
 *    are auto-derived as a fallback (see getQuickNavForContext).
 */

import {
  PanelsTopLeft, BarChart3, Target, TrendingUp, Map, AlertTriangle,
  Gauge, Shield, Siren, Bug, FileCheck, Crosshair, Building2,
  FileText, Eye, Users, Scale, Bell, Layers, Grid2x2, ListOrdered,
  Activity, ClipboardCheck, Library, Boxes, BrainCircuit,
  LayoutDashboard, FolderOpen, GraduationCap, Bot, Lock,
  CreditCard, ShieldCheck, SearchIcon,
} from 'lucide-react'

import type { ContextId } from '@/types/real-estate'
import { NAV_REGISTRY, commonNavItems, type NavItem } from '@/config/nav-registry'

export interface QuickNavItem {
  title: string
  icon: React.ElementType
  href: string
  /** Optional attention chip (keep to 1-2 per context), e.g. 'Nouveau'. */
  badge?: string
}

export const MAX_QUICK_NAV_ITEMS = 8

// ─── Curated flagship pages per persona ──────────────────────────────────────

const QUICK_NAV: Record<ContextId, QuickNavItem[]> = {

  // Vision stratégique, KPIs, board
  dirigeant: [
    { title: 'Panorama 12M', icon: PanelsTopLeft, href: '/dashboard-pro/panorama' },
    { title: 'KPIs', icon: BarChart3, href: '/dashboard-pro/kpis' },
    { title: 'Vision', icon: Target, href: '/strategy/vision' },
    { title: 'Objectifs', icon: TrendingUp, href: '/strategy/objectives' },
    { title: 'Roadmap', icon: Map, href: '/strategy/roadmap' },
    { title: 'Risques', icon: AlertTriangle, href: '/risks' },
    { title: 'Maturité', icon: Gauge, href: '/maturity' },
  ],

  // Sécurité, incidents, contrôles
  ciso: [
    { title: 'Cockpit NIST', icon: Shield, href: '/security-pro/cockpit' },
    { title: 'Incidents', icon: Siren, href: '/security-pro/incidents' },
    { title: 'Vulnérabilités', icon: Bug, href: '/security-pro/vulns' },
    { title: 'Contrôles', icon: FileCheck, href: '/security-pro/controls' },
    { title: 'Posture', icon: Crosshair, href: '/security-pro/posture' },
    { title: 'TPRM', icon: Building2, href: '/vendor-pro/cockpit' },
    { title: 'Registre Risques', icon: AlertTriangle, href: '/risk-pro/register' },
  ],

  // Vie privée, RGPD, Loi 25, consentements
  dpo: [
    { title: 'Registre Traitements', icon: FileText, href: '/compliance-pro/register' },
    { title: 'PIA (AIPD)', icon: Eye, href: '/compliance-pro/pia', badge: 'Nouveau' },
    { title: 'Demandes DSR', icon: Users, href: '/compliance-pro/dsr' },
    { title: 'Consentements', icon: FileCheck, href: '/compliance-pro/consent' },
    { title: 'RGPD', icon: Scale, href: '/compliance-pro/rgpd' },
    { title: 'Loi 25', icon: Shield, href: '/compliance-pro/loi25' },
    { title: 'Veille', icon: Bell, href: '/reg-change/alerts' },
  ],

  // Registre risques, traitement, KRI
  'risk-manager': [
    { title: 'Registre', icon: AlertTriangle, href: '/risks' },
    { title: 'Heatmap', icon: Layers, href: '/risks/heatmap' },
    { title: 'Matrice 5×5', icon: Grid2x2, href: '/risk-pro/matrix' },
    { title: 'Identifier', icon: SearchIcon, href: '/risk-pro/identify', badge: 'Nouveau' },
    { title: 'Traitement', icon: Target, href: '/risk-pro/treatment' },
    { title: 'KRI', icon: Activity, href: '/risk-pro/kri' },
    { title: 'Incidents', icon: Siren, href: '/risk-pro/incidents' },
  ],

  // Multi-réglementaire, gap analysis, audits
  'compliance-officer': [
    { title: 'Conformité', icon: Shield, href: '/compliance' },
    { title: 'RGPD', icon: Eye, href: '/compliance-pro/rgpd' },
    { title: 'Écarts', icon: AlertTriangle, href: '/compliance-pro/gaps' },
    { title: 'Actions', icon: ListOrdered, href: '/compliance-pro/actions' },
    { title: 'Missions Audit', icon: ClipboardCheck, href: '/audit-pro/missions' },
    { title: 'Référentiels', icon: Library, href: '/frameworks' },
    { title: 'Registre', icon: FileText, href: '/compliance-pro/register' },
  ],

  // Données, qualité, IA responsable
  'data-ia-officer': [
    { title: 'Cockpit DAMA', icon: BarChart3, href: '/data-pro/cockpit' },
    { title: 'Qualité', icon: Activity, href: '/data-pro/quality' },
    { title: 'MDM & Catalogue', icon: Boxes, href: '/data-pro/mdm' },
    { title: 'Registre Modèles', icon: BrainCircuit, href: '/ai-ia-pro/registre' },
    { title: 'Inventaire IA', icon: ListOrdered, href: '/ai-ia-pro/ai-inventory-register' },
    { title: 'Cockpit NIST AI', icon: Shield, href: '/ai-ia-pro/cockpit' },
    { title: 'PIA', icon: Eye, href: '/compliance/pia' },
  ],

  // Mes tâches, évaluations, signalements
  collaborateur: [
    { title: 'Mon Tableau de Bord', icon: LayoutDashboard, href: '/workspace-pro/personal-dashboard' },
    { title: 'Évaluations', icon: ClipboardCheck, href: '/assessments' },
    { title: 'Nouvelle Évaluation', icon: FileText, href: '/assessments/create', badge: 'Nouveau' },
    { title: 'Mes Tâches', icon: ListOrdered, href: '/workspace-pro/smart-task-prioritizer' },
    { title: 'Signalements', icon: FolderOpen, href: '/case-mgmt/cases' },
    { title: 'Micro-Learning', icon: GraduationCap, href: '/workspace-pro/micro-learning' },
    { title: 'Assistant', icon: Bot, href: '/workspace-pro/my-compliance-assistant' },
  ],

  // Équipe, prestataires, facturation
  administration: [
    { title: 'Utilisateurs', icon: Users, href: '/admin/users' },
    { title: 'Rôles', icon: Lock, href: '/admin/roles' },
    { title: 'Organisation', icon: Building2, href: '/admin/organization' },
    { title: 'Facturation', icon: CreditCard, href: '/self-manage/finances' },
    { title: 'Crédits IA', icon: BrainCircuit, href: '/account/credits' },
    { title: 'Trust Center', icon: ShieldCheck, href: '/admin/trust-center' },
    { title: 'Vie Privée', icon: Eye, href: '/privacy' },
  ],
}

// ─── Fallback derivation + accessor ─────────────────────────────────────────

/**
 * Fallback for uncurated/unknown contexts: first MAX leaf routes of the
 * context's sidebar nav tree (deduped), so the bar never renders empty.
 */
function deriveFromNavTree(context: ContextId): QuickNavItem[] {
  const roots: NavItem[] = NAV_REGISTRY[context] ?? commonNavItems
  const out: QuickNavItem[] = []
  const seen = new Set<string>()
  const walk = (items: NavItem[]) => {
    for (const item of items) {
      if (out.length >= MAX_QUICK_NAV_ITEMS) return
      if (item.href && !seen.has(item.href)) {
        seen.add(item.href)
        out.push({ title: item.title, icon: item.icon, href: item.href })
      }
      if (item.children) walk(item.children)
    }
  }
  walk(roots)
  return out
}

/** Quick-nav items for a context — curated first, auto-derived otherwise. */
export function getQuickNavForContext(context: ContextId): QuickNavItem[] {
  const curated = QUICK_NAV[context]
  if (curated && curated.length > 0) return curated.slice(0, MAX_QUICK_NAV_ITEMS)
  return deriveFromNavTree(context)
}
